/**
 * The one action word of a queue row (minimal redesign, 12 Oct 2026).
 *
 * The Action column says what the next step on the case is, in the same order the
 * case page decides its primary action (`case-review/case-action.ts`), from the fields
 * a queue row carries: state, work status, exceptions, the line counts, the reply
 * flags, the duplicate marker and the intent. The words are literal strings of the
 * build contract; the case page and the training use the same ones.
 *
 * Two states of the case page have no field on the row: a reply draft made before the
 * person's later changes (the page says "Prepare again", the row says "Copy reply")
 * and a blocked check of the order-system preview (the page says "Check: <reason>",
 * the row says "Check" only when a blocking rule flag is on the row). A backend
 * `next_action` field would make both words identical; that is a follow-up lane.
 */

import { REASON_CUSTOMER, REASON_ENGINE_FAILED } from "@/lib/case-status";
import { isOutOfScopeCase } from "@/lib/intents";
import { type QuoteCaseSummary } from "@/lib/mock";
import { confirmsInOneClick } from "@/lib/sales-flow";

/**
 * The action words, exactly as the contract spells them. "Pick the customer" is the
 * label of record of the case page's big button (`ACTION_LABELS.chooseCustomer`): a
 * suggested account without evidence, or two accounts that fit, is picked there with
 * the mail in view (round 5, R5-M-05: 27 of 50 live rows said "Find customer" for a case
 * whose page offered "Confirm" and "Pick the customer").
 */
export const ACTION_WORD = {
  reading: "Reading",
  couldNotRead: "Could not read",
  nothing: "Nothing to do",
  findCustomer: "Find customer",
  pickCustomer: "Pick the customer",
  confirm: "Confirm",
  check: "Check",
  prepareReply: "Prepare reply",
  copyReply: "Copy reply",
} as const;

/** "Pick 3", "Pick 1": the items a person has to confirm or pick. */
export function pickWord(count: number): string {
  return `Pick ${count}`;
}

/** The three words that name no work for the viewer: the badge stays neutral. */
const NO_WORK_WORDS: readonly string[] = [
  ACTION_WORD.reading,
  ACTION_WORD.couldNotRead,
  ACTION_WORD.nothing,
];

/** The fields of a row the action word reads; every one is optional on an older answer. */
export type ActionWordRow = Pick<
  QuoteCaseSummary,
  | "state"
  | "workStatus"
  | "exceptions"
  | "lineCount"
  | "linesAuto"
  | "replyFound"
  | "replyDraftReady"
  | "duplicateOf"
  | "nearDuplicateOf"
  | "intent"
  | "engineIntent"
  | "route"
  | "reviewReason"
  | "couldNotProcess"
  | "customerToConfirm"
  | "suggestedCustomers"
  | "customer"
  | "ruleFlagGroups"
>;

/** True while the row's work status is open (an older answer without one counts as open). */
function isOpen(row: ActionWordRow): boolean {
  return (row.workStatus ?? "open") === "open";
}

/** True when the customer of the row is still to be settled by a person. */
function customerOpen(row: ActionWordRow): boolean {
  return (
    row.customerToConfirm === true ||
    row.route === "ask-customer" ||
    row.reviewReason === REASON_CUSTOMER
  );
}

/** The lines a person still has to confirm or pick: every line I2Q is not sure of. */
export function linesToDecide(row: Pick<ActionWordRow, "lineCount" | "linesAuto">): number {
  const total = row.lineCount ?? 0;
  if (total <= 0) {
    return 0;
  }
  return Math.max(0, total - Math.min(total, row.linesAuto ?? 0));
}

/** True when a rule holds the case or an exception blocks it. */
function blocked(row: ActionWordRow): boolean {
  if ((row.exceptions ?? []).length > 0) {
    return true;
  }
  return (row.ruleFlagGroups ?? []).some(
    (group) => group.severity === "blocking" && group.count > 0,
  );
}

/**
 * The action word of a row, first match wins:
 * Reading, Could not read, Nothing to do, Find customer, Pick the customer, Confirm,
 * Pick n, Check, Prepare reply, Copy reply.
 *
 * The customer words follow `primaryActionOf` on the case page: no suggestion is
 * "Find customer"; a first suggestion with evidence is "Confirm"; a guess, or two
 * accounts that fit, is "Pick the customer".
 */
export function queueActionWord(row: ActionWordRow): string {
  if (row.state === "queued" || row.state === "processing") {
    return ACTION_WORD.reading;
  }
  if (row.couldNotProcess === true || row.reviewReason === REASON_ENGINE_FAILED) {
    return ACTION_WORD.couldNotRead;
  }
  // Out of scope, a copy of another mail, closed or answered: nothing for a person here.
  if (
    row.state === "duplicate" ||
    row.state === "auto-closed" ||
    !!row.duplicateOf ||
    !!row.nearDuplicateOf ||
    isOutOfScopeCase(row) ||
    !isOpen(row) ||
    row.replyFound === true
  ) {
    return ACTION_WORD.nothing;
  }
  if (customerOpen(row)) {
    const suggestion = row.suggestedCustomers?.[0];
    if (!suggestion) {
      return ACTION_WORD.findCustomer;
    }
    // One click only for a suggestion with evidence; a guess, or two accounts that fit,
    // is picked on the case page with the mail in view, as its big button says.
    return confirmsInOneClick(suggestion) ? ACTION_WORD.confirm : ACTION_WORD.pickCustomer;
  }
  const open = linesToDecide(row);
  if (open > 0) {
    return pickWord(open);
  }
  if (blocked(row)) {
    return ACTION_WORD.check;
  }
  return row.replyDraftReady === true ? ACTION_WORD.copyReply : ACTION_WORD.prepareReply;
}

/** True when the word names work a person has to do (the badge may then be accent). */
export function isWorkWord(word: string): boolean {
  return !NO_WORK_WORDS.includes(word);
}

/**
 * The tone of the Action badge: accent for the viewer's own open work, neutral for
 * everything else (a colleague's case, nobody's case, nothing to do).
 */
export function actionTone(
  word: string,
  assignee: string | null | undefined,
  viewer: string | null | undefined,
): "accent" | "neutral" {
  const mine =
    !!assignee && !!viewer && assignee.trim().toLowerCase() === viewer.trim().toLowerCase();
  return mine && isWorkWord(word) ? "accent" : "neutral";
}
